import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import type { UserRelation } from '../../types/api'
import { Avatar } from '../ui'

interface WaitingForTrainingSetWidgetProps {
  coach: UserRelation | null
  coachCount?: number
}

function coachDisplayName(coach: UserRelation | null, fallback: string): string {
  if (!coach) return fallback
  if (coach.firstName && coach.lastName) return `${coach.firstName} ${coach.lastName}`
  if (coach.firstName) return coach.firstName
  if (coach.username) return `@${coach.username}`
  return fallback
}

/** Hero card when trainee has a coach but no active training set yet. */
export default function WaitingForTrainingSetWidget({
  coach,
  coachCount = 1,
}: WaitingForTrainingSetWidgetProps) {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const name = coachDisplayName(coach, t.trainingSetDetail.relatedUser.coachLabel)
  const copy = t.traineeDashboard.emptyState.waitingCoach

  return (
    <div
      data-tour="tour-trainee-training-set"
      className="mb-6 rounded-2xl cursor-pointer transition-all active:scale-[0.98] relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #005f8f 0%, #0088cc 55%, #3ba8dc 100%)',
        boxShadow: '0 8px 32px rgba(0, 136, 204, 0.35)',
      }}
      onClick={() => navigate('/my-coaches')}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          navigate('/my-coaches')
        }
      }}
    >
      <div
        className="absolute pointer-events-none"
        style={{
          width: 160,
          height: 160,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
          top: -40,
          right: -40,
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
          bottom: -20,
          right: 60,
        }}
      />

      <div className="relative p-5">
        <div className="flex items-center justify-between mb-4">
          <span
            className="text-xs font-semibold tracking-widest uppercase"
            style={{ color: 'rgba(255,255,255,0.65)' }}
          >
            {copy.badge}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 18 }}>→</span>
        </div>

        <div className="flex items-center gap-3 mb-5">
          <div
            style={{
              padding: 2,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.25)',
              flexShrink: 0,
            }}
          >
            <Avatar
              photoUrl={coach?.photoUrl}
              name={name}
              size="md"
              fallbackBg="rgba(255,255,255,0.2)"
            />
          </div>
          <div className="min-w-0">
            <div
              className="text-xs font-medium mb-0.5"
              style={{ color: 'rgba(255,255,255,0.65)' }}
            >
              {t.trainingSetDetail.relatedUser.coachLabel}
              {coachCount > 1 ? ` · ${coachCount}` : ''}
            </div>
            <div className="text-lg font-bold truncate" style={{ color: 'white' }}>
              {name}
            </div>
          </div>
        </div>

        <div
          className="rounded-xl p-3.5"
          style={{
            background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.18)',
          }}
        >
          <div className="text-sm font-bold mb-1" style={{ color: 'white' }}>
            {copy.title}
          </div>
          <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.78)' }}>
            {copy.subtitle}
          </p>
          <div
            className="mt-3 text-xs font-semibold"
            style={{ color: 'rgba(255,255,255,0.95)' }}
          >
            {copy.viewCoaches} →
          </div>
        </div>
      </div>
    </div>
  )
}
